import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Avatar,
  Button,
  EmptyState,
  ListGroup,
  ListRow,
  MoneyField,
  SegmentedControl,
  Skeleton,
  Switch,
  TextField,
} from '@uplink/ui';
import { useState } from 'react';
import { errorText, useTRPC } from '../api.ts';
import { parseUsdInput } from '../format.ts';
import { MainActionBar } from '../native-controls.tsx';
import type { Nav } from '../nav.ts';
import { usePrefs } from '../prefs.tsx';
import { haptic, openChat } from '../telegram.ts';
import {
  AmountStatus,
  DOMAIN_STATUS,
  ORDER_KIND,
  ORDER_STATUS,
  SERVER_STATUS,
  StatusBadge,
  providerName,
} from './common.tsx';

type Mode = 'credit' | 'debit';

export function AdminUserScreen({ id }: { nav: Nav; id: string }) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { usd, date, dateTime } = usePrefs();
  const user = useQuery(trpc.admin.user.queryOptions({ id }));

  // The balance form: open while `mode` is set; a new key per attempt makes a retried request harmless
  const [mode, setMode] = useState<Mode | null>(null);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [notify, setNotify] = useState(true);
  const [key, setKey] = useState(() => crypto.randomUUID());
  const [done, setDone] = useState<string | null>(null);
  const cents = parseUsdInput(amount);
  const valid = !!cents && cents > 0 && reason.trim().length > 0;

  const adjust = useMutation(
    trpc.admin.adjust.mutationOptions({
      onSuccess: (res) => {
        haptic.success();
        setDone(
          `${mode === 'credit' ? 'Начислено' : 'Списано'} ${usd(cents ?? 0)}. Баланс: ${usd(res.balanceCents)}`,
        );
        setMode(null);
        setAmount('');
        setReason('');
        setKey(crypto.randomUUID());
        queryClient.invalidateQueries(trpc.admin.pathFilter());
      },
      onError: () => haptic.error(),
    }),
  );

  const open = (m: Mode) => {
    haptic.tap();
    setDone(null);
    setMode(m);
  };

  if (user.isPending) {
    return (
      <main className="ul-screen">
        <Skeleton height={140} />
        <Skeleton height={180} />
      </main>
    );
  }
  if (user.isError || !user.data) {
    return (
      <main className="ul-screen">
        <EmptyState
          icon="user"
          title="Пользователь не найден"
          text={user.isError ? errorText(user.error) : undefined}
        />
      </main>
    );
  }

  const u = user.data;
  const name = [u.firstName, u.lastName].filter(Boolean).join(' ');

  return (
    <main className={mode ? 'ul-screen ul-screen--with-action' : 'ul-screen'}>
      <header className="ul-profile">
        <Avatar src={u.photoUrl} name={u.firstName} size={72} />
        <span className="ul-profile__name">{name}</span>
        <span className="ul-profile__meta">
          {u.username ? `@${u.username} · ` : ''}ID {u.telegramId}
        </span>
        <span className="ul-profile__meta">
          {u.lang === 'ru' ? 'Русский' : 'English'} · {u.currency} · с {date(u.createdAt)} · был{' '}
          {dateTime(u.lastSeenAt)}
        </span>
      </header>

      <section className="ul-bal">
        <span className="ul-bal__label">Баланс</span>
        <span className="ul-bal__amount">{usd(u.balanceCents)}</span>
        <span className="ul-bal__note ul-bal__note--start">
          {u.holdCents > 0 ? `В оплате заказов ${usd(u.holdCents)} · ` : ''}
          Пополнения: {usd(u.topups.amount)} · платежей {u.topups.count}
        </span>
        <div className="ul-row-actions">
          <Button variant="secondary" icon="plus" onClick={() => open('credit')}>
            Начислить
          </Button>
          <Button variant="secondary" icon="minus" onClick={() => open('debit')}>
            Списать
          </Button>
        </div>
      </section>

      {done ? (
        <div className="ul-success-note" role="status">
          {done}
        </div>
      ) : null}

      {mode ? (
        <section className="ul-stack">
          <SegmentedControl<Mode>
            label="Операция"
            value={mode}
            onChange={setMode}
            options={[
              { value: 'credit', label: 'Начислить' },
              { value: 'debit', label: 'Списать' },
            ]}
          />
          <MoneyField
            id="adjust-amount"
            label="Сумма"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
          <TextField
            id="adjust-reason"
            label="Причина"
            value={reason}
            onChange={setReason}
            placeholder={mode === 'credit' ? 'Компенсация за простой сервера' : 'Ошибочное начисление'}
            hint={notify ? 'Пользователь увидит её в уведомлении' : 'Видна только в админке'}
          />
          <ListGroup>
            <ListRow
              icon="megaphone"
              title="Уведомить в боте"
              subtitle="Сумма, причина и новый баланс"
              trailing={<Switch checked={notify} onChange={setNotify} label="Уведомить в боте" />}
            />
          </ListGroup>
          {adjust.isError ? (
            <div className="ul-error" role="alert">
              {errorText(adjust.error)}
            </div>
          ) : null}
          <Button variant="ghost" onClick={() => setMode(null)}>
            Отмена
          </Button>
          <MainActionBar
            action={{
              text: valid
                ? `${mode === 'credit' ? 'Начислить' : 'Списать'} ${usd(cents!)}`
                : 'Укажите сумму и причину',
              disabled: !valid,
              loading: adjust.isPending,
              onClick: () =>
                valid &&
                adjust.mutate({
                  userId: u.id,
                  amountCents: mode === 'credit' ? cents! : -cents!,
                  reason: reason.trim(),
                  notify,
                  key,
                }),
            }}
          />
        </section>
      ) : null}

      {u.username ? (
        <ListGroup>
          <ListRow icon="support" title="Написать в Telegram" chevron onClick={() => openChat(u.username!)} />
        </ListGroup>
      ) : null}

      {u.servers.length ? (
        <ListGroup header={`Серверы · ${u.servers.length}`}>
          {u.servers.map((s) => (
            <ListRow
              key={s.id}
              icon="server"
              title={s.name}
              subtitle={[
                `${providerName(s.provider)} · ${s.location}`,
                `тариф ${s.planId} · upstream ${s.upstreamPlanId ?? '—'}`,
                s.ipv4,
                s.paidUntil ? `до ${date(s.paidUntil)}` : null,
              ]
                .filter(Boolean)
                .join(' · ')}
              trailing={
                <AmountStatus amount={`${usd(s.priceCents)}/мес`} map={SERVER_STATUS} status={s.status} />
              }
            />
          ))}
        </ListGroup>
      ) : null}

      {u.domains.length ? (
        <ListGroup header={`Домены · ${u.domains.length}`}>
          {u.domains.map((d) => (
            <ListRow
              key={d.id}
              icon="globe"
              title={d.name}
              subtitle={[d.expiresAt ? `до ${date(d.expiresAt)}` : null, d.autoRenew ? 'автопродление' : null]
                .filter(Boolean)
                .join(' · ')}
              trailing={<StatusBadge map={DOMAIN_STATUS} status={d.status} />}
            />
          ))}
        </ListGroup>
      ) : null}

      {u.orders.length ? (
        <div className="ul-multiline">
          <ListGroup header="Заказы">
            {u.orders.map((o) => (
              <ListRow
                key={o.id}
                icon={o.kind.startsWith('server') ? 'server' : 'globe'}
                title={ORDER_KIND[o.kind] ?? o.kind}
                subtitle={`${dateTime(o.createdAt)}
маржа ${usd(o.amountCents - o.costCents)}`}
                trailing={<AmountStatus amount={usd(o.amountCents)} map={ORDER_STATUS} status={o.status} />}
              />
            ))}
          </ListGroup>
        </div>
      ) : null}

      <ListGroup header="История баланса">
        {u.history.length === 0 ? (
          <ListRow icon="clock" title="Движений пока не было" />
        ) : (
          u.history.map((h) => (
            <ListRow
              key={h.id}
              icon={h.amountCents > 0 ? 'arrowDown' : 'arrowUp'}
              title={h.title}
              subtitle={[dateTime(h.createdAt), h.note].filter(Boolean).join(' · ')}
              value={
                <span className={h.amountCents > 0 ? 'ul-history-amount--in' : undefined}>
                  {h.amountCents > 0 ? '+' : ''}
                  {usd(h.amountCents)}
                </span>
              }
            />
          ))
        )}
      </ListGroup>
    </main>
  );
}
